/**
 * One board. Spec 11.4: "CRUD boards and squares, arrow/snake maps, versioning, activation."
 *
 * Read-only for now, and that is a deliberate boundary rather than an omission. The square
 * upsert procedure exists (`adminBoardSquareUpsertInput` in @lila/shared), but editing a
 * board's topology through a form is the change most likely to strand a square or break
 * termination — and unlike a rule set, a board has no dry-run gate: `validateBoard` checks
 * shape, and reachability is only meaningful against a rule set. Wiring board edits to the
 * same simulator gate is the right design and is written up rather than half-built.
 */
import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { Card } from '../../../components/ui/Card';
import { DataTable, type Column } from '../../../components/ui/DataTable';
import { EmptyState } from '../../../components/ui/EmptyState';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { useAdminContent } from '../../../features/admin/api';
import { useBoard } from '../../../features/board/api';
import { errorKeyFor } from '../../../lib/errors';
import { isLocale } from '../../../lib/i18n';

export const Route = createFileRoute('/$lang/admin/content/boards/$id')({ component: Page });

type SquareRow = {
  readonly number: number;
  readonly key: string;
  readonly arrowTo: number | null;
  readonly snakeTo: number | null;
};

function Page(): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const { lang, id } = Route.useParams();

  const content = useAdminContent();
  const board = useBoard(isLocale(lang) ? lang : 'en', id);

  const row = content.data?.boards.find((entry) => entry.id === id);

  const columns: readonly Column<SquareRow>[] = [
    {
      id: 'number',
      header: t('admin:content.squares'),
      align: 'end',
      cell: (square) => square.number,
    },
    { id: 'key', header: 'key', cell: (square) => square.key },
    {
      id: 'arrow',
      header: '↑',
      align: 'end',
      cell: (square) => square.arrowTo ?? '',
    },
    {
      id: 'snake',
      header: '↓',
      align: 'end',
      cell: (square) => square.snakeTo ?? '',
    },
  ];

  if (board.error !== null) return <Alert tone="error">{t(errorKeyFor(board.error))}</Alert>;
  if (board.isLoading || board.data === undefined) {
    return <SkeletonList rows={5} label={t('admin:nav.boards')} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">{board.data.name}</h1>
          <p className="text-sm text-ink-muted">
            {board.data.slug} · v{board.data.version}
          </p>
        </div>
        {row !== undefined && (
          <Badge tone={row.isActive ? 'positive' : 'neutral'}>
            {row.isActive ? t('admin:content.active') : t('admin:content.draft')}
          </Badge>
        )}
      </header>

      <Card title={t('admin:content.squares')}>
        <DataTable
          caption={t('admin:content.squares')}
          columns={columns}
          rows={board.data.squares}
          rowKey={(square) => String(square.number)}
          empty={<EmptyState title={t('admin:content.draft')} />}
        />
      </Card>
    </div>
  );
}
